import { Button } from '@/components/ui/button'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import Section from 'app/frontend/base/components/Section'
import OverviewTileType from 'app/frontend/types/custom/OverviewTileType'
import { Settings } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Overview from 'app/frontend/base/pages/TeamDashboard/subpages/TeamOverview/components/Overview'
import EditableOverview from 'app/frontend/base/pages/TeamDashboard/subpages/TeamOverview/components/EditableOverview'
import {
  OverviewConfigContext,
  OverviewRuntimeContext,
  OverviewRuntimeContextType,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamOverview/context'
import {
  FormValues,
  formSchema,
  MAIN_FIELD,
  SIDEBAR_FIELD,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamOverview/form'
import { OverviewTile } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamOverview/defaults'

type OverviewEntity = {
  id: number
  mainOverviewTiles: OverviewTile[]
  sidebarOverviewTiles: OverviewTile[]
}

type Props<T extends OverviewEntity> = {
  title: string
  entity: T
  entityType: string
  isAdmin: boolean
  defaultTiles: OverviewTile[]
  allowedTileTypes: readonly OverviewTileType[]
  runtime: OverviewRuntimeContextType
  sidebarContent?: ReactNode
}

const OverviewPage = <T extends OverviewEntity>({
  title,
  entity,
  entityType,
  isAdmin,
  defaultTiles,
  allowedTileTypes,
  runtime,
  sidebarContent,
}: Props<T>) => {
  const [isCustomizing, setIsCustomizing] = useState(false)
  const queryClient = useQueryClient()
  const hasStoredTiles =
    entity.mainOverviewTiles.concat(entity.sidebarOverviewTiles).length > 0
  const overviewDataQueryKey =
    entityType === 'guild' ? 'guildOverviewData' : 'overviewData'

  const initialMainTiles = useMemo<OverviewTile[]>(() => {
    return hasStoredTiles
      ? entity.mainOverviewTiles
      : defaultTiles.filter((tile) => tile.position === 'main')
  }, [defaultTiles, entity.mainOverviewTiles, hasStoredTiles])

  const initialSidebarTiles = useMemo<OverviewTile[]>(() => {
    return hasStoredTiles
      ? entity.sidebarOverviewTiles
      : defaultTiles.filter((tile) => tile.position === 'sidebar')
  }, [defaultTiles, entity.sidebarOverviewTiles, hasStoredTiles])

  const config = useMemo(
    () => ({ allowedTileTypes, defaultTiles }),
    [allowedTileTypes, defaultTiles],
  )

  const content = !isCustomizing ? (
    <>
      {isAdmin && (
        <h2 className='flex items-center justify-end'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setIsCustomizing(true)}
          >
            <Settings className='h-4 w-4 mr-2' />
            Customize
          </Button>
        </h2>
      )}

      <Overview
        mainTiles={initialMainTiles}
        sidebarTiles={initialSidebarTiles}
        isCustomizing={false}
        sidebarContent={sidebarContent}
      />
    </>
  ) : (
    <FormWrapper<T & FormValues>
      formSchema={formSchema}
      entityType={entityType}
      entity={{
        ...entity,
        [MAIN_FIELD]: initialMainTiles,
        [SIDEBAR_FIELD]: initialSidebarTiles,
      }}
      onSave={() => {
        setIsCustomizing(false)
        queryClient.invalidateQueries({
          queryKey: [overviewDataQueryKey, { id: entity.id }],
        })
      }}
      transformValues={(values) => ({
        ...values,
        [MAIN_FIELD]: values[MAIN_FIELD].map(
          (tile: OverviewTile, index: number) => ({
            ...tile,
            order: index,
          }),
        ),
        [SIDEBAR_FIELD]: values[SIDEBAR_FIELD].map(
          (tile: OverviewTile, index: number) => ({
            ...tile,
            order: index,
          }),
        ),
      })}
      submitButtons={() => null}
    >
      <EditableOverview onCancel={() => setIsCustomizing(false)} />
    </FormWrapper>
  )

  return (
    <Section title={title}>
      <OverviewRuntimeContext.Provider value={runtime}>
        <OverviewConfigContext.Provider value={config}>
          {content}
        </OverviewConfigContext.Provider>
      </OverviewRuntimeContext.Provider>
    </Section>
  )
}

export default OverviewPage
